import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { fetchSymptoms } from "../../services/LabTestApi/symptomsApi";
import { FaChevronLeft, FaChevronRight, FaStethoscope, FaStar } from "react-icons/fa";

import useEmblaCarousel from "embla-carousel-react";
import Autoplay from "embla-carousel-autoplay";

import fallbackImg from "../assets/logo/appstore.png";

export default function LabSymptoms() {
  const [symptoms, setSymptoms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isReady, setIsReady] = useState(false);

  const navigate = useNavigate();

  // ✅ Image base URL
  const API_BASE = process.env.REACT_APP_API_URL?.replace(/\/api\/?$/, '');
  const IMAGE_BASE = API_BASE ? `${API_BASE}/SymptomsImage/` : '';

  // ✅ Embla setup with Autoplay
  const [emblaRef, emblaApi] = useEmblaCarousel(
    {
      align: "start",
      containScroll: "trimSnaps",
      loop: true,
      dragFree: false,
      slidesToScroll: 1,
      breakpoints: {
        '(min-width: 640px)': { slidesToScroll: 2 },
        '(min-width: 768px)': { slidesToScroll: 3 },
        '(min-width: 1024px)': { slidesToScroll: 4 },
        '(min-width: 1280px)': { slidesToScroll: 5 },
      }
    },
    [
      Autoplay({
        delay: 3000,
        stopOnInteraction: true,
        stopOnMouseEnter: true,
      })
    ]
  );

  // ✅ When emblaApi is ready
  useEffect(() => {
    if (emblaApi) {
      setIsReady(true);
      setSelectedIndex(emblaApi.selectedScrollSnap());
    } else {
      setIsReady(false);
    }
  }, [emblaApi]);

  // ✅ Update selected index on slide change
  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => setSelectedIndex(emblaApi.selectedScrollSnap());
    emblaApi.on("select", onSelect);
    return () => emblaApi.off("select", onSelect);
  }, [emblaApi]);

  // ✅ Manual scroll functions
  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext();
  }, [emblaApi]);

  const scrollTo = useCallback((index) => {
    if (emblaApi) emblaApi.scrollTo(index);
  }, [emblaApi]);

  // ✅ Fetch symptoms - ONLY ACTIVE (isactive === true)
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const data = await fetchSymptoms();
        console.log("📦 All Symptoms:", data);
        
        if (!Array.isArray(data)) {
          console.warn("⚠️ Data is not an array:", data);
          setSymptoms([]);
          setLoading(false);
          return;
        }
        
        const activeSymptoms = data.filter(item => {
          const isActive = item.isActive === true || 
                          item.isActive === "true" || 
                          item.isactive === true || 
                          item.isactive === "true";
          return isActive;
        });
        
        console.log("✅ Active Symptoms Found:", activeSymptoms.length);
        console.log("📌 Active Symptoms:", activeSymptoms.map(s => s.name));
        
        setSymptoms(activeSymptoms);
        
      } catch (err) {
        console.error("Failed to load symptoms", err);
        setSymptoms([]);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  // ✅ Re-initialize Embla when symptoms change
  useEffect(() => {
    if (emblaApi && symptoms.length > 0) {
      setTimeout(() => {
        emblaApi.reInit();
        console.log("🔄 LabSymptoms Embla re-initialized");
      }, 200);
    }
  }, [symptoms, emblaApi]);

  // ✅ Loading state
  if (loading) {
    return (
      <div className="px-4 md:px-8 py-6 bg-white rounded-xl shadow-lg mt-3">
        <div className="text-center">
          <div className="inline-flex items-center gap-2 bg-gradient-to-r from-red-500 to-red-600 text-white px-4 py-1.5 rounded-full shadow-md mb-4">
            <span className="w-1.5 h-1.5 bg-yellow-400 rounded-full animate-pulse"></span>
            <span className="text-xs font-bold tracking-wide">⏳ LOADING SYMPTOMS</span>
          </div>
          <div className="animate-pulse">
            <div className="flex justify-center gap-4">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="w-[110px] h-[140px] bg-gray-200 rounded-xl"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (symptoms.length === 0) {
    return (
      <div className="px-4 md:px-8 py-6 bg-white rounded-xl shadow-lg mt-3 text-center">
        <div className="inline-flex items-center gap-2 bg-gradient-to-r from-red-500 to-red-600 text-white px-4 py-1.5 rounded-full shadow-md mb-4">
          <FaStethoscope size={12} className="text-yellow-400" />
          <span className="text-xs font-bold tracking-wide">HEALTH CONCERNS</span>
        </div>
        <p className="text-gray-500 mt-2">No active symptoms found</p>
        <button 
          onClick={() => window.location.reload()}
          className="mt-4 text-xs text-red-500 hover:text-red-600 font-semibold underline"
        >
          Retry Loading
        </button>
      </div>
    );
  }

  return (
    <div className="px-4 md:px-8 py-6 bg-white rounded-xl shadow-lg mt-3">
      
      {/* Header Section */}
      <div className="text-center mb-6">
     
        
        <h2 className="text-2xl md:text-3xl lg:text-4xl font-extrabold">
          <span className="text-gray-800">Test by</span>{" "}
          <span className="text-red-600">Health Concerns</span>
        </h2>
        
        <p className="text-gray-500 text-sm mt-2 max-w-md mx-auto">
          Find the right test based on your symptoms
        </p>
        
        <p className="text-xs text-gray-400 mt-3">
          {symptoms.length} active health concerns
        </p>
        
        <div className="flex justify-center gap-2 mt-4">
          <div className="w-1.5 h-1.5 rounded-full bg-red-300"></div>
          <div className="w-6 h-1.5 rounded-full bg-red-500"></div>
          <div className="w-1.5 h-1.5 rounded-full bg-red-300"></div>
          <div className="w-1.5 h-1.5 rounded-full bg-red-200"></div>
          <div className="w-1.5 h-1.5 rounded-full bg-red-200"></div>
        </div>
      </div>

      {/* ✅ Embla Carousel Section - Both arrows always visible */}
      <div className="relative group w-full px-8">
        {/* ✅ Left Arrow - Always Visible */}
        <button
          onClick={scrollPrev}
          className="absolute left-0 top-1/2 -translate-y-1/2 z-20 
          bg-white hover:bg-red-500 text-red-500 hover:text-white 
          shadow-lg p-2.5 rounded-full cursor-pointer transition-all duration-300 
          hover:scale-110 focus:outline-none"
          aria-label="Previous"
        >
          <FaChevronLeft size={16} />
        </button>

        <div className="overflow-hidden w-full" ref={emblaRef}>
          <div className="flex gap-4">
            {symptoms.map((item) => {
              const imageUrl = item?.symptoms_Image
                ? `${IMAGE_BASE}${item.symptoms_Image}`
                : null;

              return (
                <div
                  key={item.id}
                  onClick={() =>
                    navigate(`/SymptomsDetails/${item.id}`, {
                      state: { symptom: item },
                    })
                  }
                  className="min-w-[110px] max-w-[110px] flex-shrink-0 cursor-pointer transform transition-all duration-300 hover:-translate-y-1"
                >
                  <div className="relative w-full h-28 rounded-xl overflow-hidden bg-gradient-to-br from-gray-100 to-gray-200 shadow-md hover:shadow-xl transition-all duration-300">
                    <img
                      src={imageUrl || fallbackImg}
                      alt={item.name}
                      className="w-full h-full object-cover transition-transform duration-500 hover:scale-110"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = fallbackImg;
                      }}
                      loading="lazy"
                    />
                    
                    <div className="absolute inset-0 bg-gradient-to-t from-red-600/60 via-transparent to-transparent 
                    opacity-0 hover:opacity-100 transition-opacity duration-300" />
                    
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 hover:opacity-100 transition-all duration-300">
                      <span className="text-white text-[10px] font-bold bg-black/50 px-2 py-1 rounded-full">
                        View Tests
                      </span>
                    </div>
                  </div>

                  <p className="text-xs md:text-sm mt-2 text-center font-semibold text-gray-700 hover:text-red-600 transition-colors duration-300 line-clamp-2 min-h-[40px]">
                    {item.name}
                  </p>
                </div>
              );
            })}
          </div>
        </div>

        {/* ✅ Right Arrow - Always Visible */}
        <button
          onClick={scrollNext}
          className="absolute right-0 top-1/2 -translate-y-1/2 z-20 
          bg-white hover:bg-red-500 text-red-500 hover:text-white 
          shadow-lg p-2.5 rounded-full cursor-pointer transition-all duration-300 
          hover:scale-110 focus:outline-none"
          aria-label="Next"
        >
          <FaChevronRight size={16} />
        </button>
      </div>

      {/* ✅ Dots */}
      {isReady && symptoms.length > 1 && (
        <div className="flex justify-center gap-2 mt-4">
          {symptoms.map((_, index) => (
            <button
              key={index}
              onClick={() => scrollTo(index)}
              className={`transition-all duration-300 rounded-full ${
                selectedIndex === index
                  ? "w-6 h-1.5 bg-gradient-to-r from-red-500 to-red-600"
                  : "w-1.5 h-1.5 bg-red-300 hover:bg-red-400"
              }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      )}

      {/* Trust Badges */}
      {symptoms.length > 0 && (
        <div className="mt-6 pt-4 border-t border-gray-200">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <div className="flex items-center justify-center gap-1.5 text-[10px] text-gray-600 bg-gray-50 rounded-lg p-1.5 hover:bg-red-50 transition-colors">
              <div className="w-5 h-5 bg-green-100 rounded-full flex items-center justify-center">
                <span className="text-green-600 text-[9px] font-bold">✓</span>
              </div>
              <span>{symptoms.length} Active Concerns</span>
            </div>
            <div className="flex items-center justify-center gap-1.5 text-[10px] text-gray-600 bg-gray-50 rounded-lg p-1.5 hover:bg-red-50 transition-colors">
              <div className="w-5 h-5 bg-blue-100 rounded-full flex items-center justify-center">
                <span className="text-blue-600 text-[9px] font-bold">⚡</span>
              </div>
              <span>Accurate Tests</span>
            </div>
            <div className="flex items-center justify-center gap-1.5 text-[10px] text-gray-600 bg-gray-50 rounded-lg p-1.5 hover:bg-red-50 transition-colors">
              <div className="w-5 h-5 bg-purple-100 rounded-full flex items-center justify-center">
                <span className="text-purple-600 text-[9px] font-bold">🏆</span>
              </div>
              <span>Trusted by Doctors</span>
            </div>
            <div className="flex items-center justify-center gap-1.5 text-[10px] text-gray-600 bg-gray-50 rounded-lg p-1.5 hover:bg-red-50 transition-colors">
              <div className="w-5 h-5 bg-red-100 rounded-full flex items-center justify-center">
                <span className="text-red-600 text-[9px] font-bold">❤️</span>
              </div>
              <span>Quick Results</span>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.6; transform: scale(1.05); }
        }
        .animate-pulse {
          animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }
      `}</style>
    </div>
  );
}